import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { useAuth } from "./auth";
import { setAthleticYear } from "./pdfLogo";

const SchoolContext = createContext(null);

function hexToHsl(hex) {
  if (!hex) return null;
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hh = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hh = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hh = (b - r) / d + 2;
        break;
      default:
        hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hexToRgb(hex) {
  if (!hex) return null;
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

export function applySchoolColors(settings) {
  if (!settings) return;
  const root = document.documentElement;
  const primaryHsl = hexToHsl(settings.primary_color);
  const primaryRgb = hexToRgb(settings.primary_color);
  const secondaryHsl = hexToHsl(settings.secondary_color);
  const accentHsl = hexToHsl(settings.accent_color);
  if (primaryHsl) root.style.setProperty("--school-primary", primaryHsl);
  if (primaryRgb) root.style.setProperty("--school-primary-rgb", primaryRgb);
  if (secondaryHsl) root.style.setProperty("--school-secondary", secondaryHsl);
  if (accentHsl) root.style.setProperty("--school-accent", accentHsl);
}

export function SchoolProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [schools, setSchools] = useState([]);
  const [activeSchoolId, setActiveSchoolId] = useState(
    () => localStorage.getItem("omni_active_school") || null
  );

  const isSuper = user?.role === "super_admin";

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings/school");
    setSettings(data);
    applySchoolColors(data);
    setAthleticYear(data?.athletic_year);
    return data;
  }, []);

  const refreshSchools = useCallback(async () => {
    const { data } = await api.get("/schools");
    setSchools(data);
    return data;
  }, []);

  // Resolve active school whenever the user changes (after login / refresh)
  useEffect(() => {
    if (!user) {
      setSettings(null);
      setSchools([]);
      return;
    }
    let cancelled = false;
    (async () => {
      let sid = localStorage.getItem("omni_active_school");
      if (isSuper) {
        try {
          const list = await refreshSchools();
          if (cancelled) return;
          if (!sid || !list.find((s) => s.id === sid)) sid = list[0]?.id || null;
        } catch {
          /* ignore */
        }
      } else {
        sid = user.school_id || null;
      }
      if (sid) {
        localStorage.setItem("omni_active_school", sid);
        if (!cancelled) setActiveSchoolId(sid);
        try {
          await loadSettings();
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isSuper, loadSettings, refreshSchools]);

  const setActiveSchool = useCallback(
    async (id) => {
      localStorage.setItem("omni_active_school", id);
      setActiveSchoolId(id);
      try {
        await loadSettings();
      } catch {
        /* ignore */
      }
    },
    [loadSettings]
  );

  const save = async (payload) => {
    const { data } = await api.put("/settings/school", payload);
    setSettings(data);
    applySchoolColors(data);
    return data;
  };

  return (
    <SchoolContext.Provider
      value={{
        settings,
        schools,
        activeSchoolId,
        isSuper,
        setActiveSchool,
        refreshSchools,
        save,
        reload: loadSettings,
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
}

export const useSchool = () => useContext(SchoolContext);
